import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import type { RecordOpen } from '../tasks/model.ts';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';
import {
  TENURES, useAddProperty, useClientProperties,
  type ClientProperty, type NewProperty, type PropertyType, type Tenure,
} from '../api/properties.ts';
import { AddressFields, NeighbourhoodField } from './PlacePicker.tsx';
import { PropertyAddress } from './PropertyAddress.tsx';
import { propertyPath } from '../tasks/taskView.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

const TENURE_WORDS: Record<Tenure, string> = {
  Owned: 'Owns it',
  Rented: 'Rents it',
  Devised: 'Devised to them',
  'Life Estate': 'Life estate',
  Unknown: 'Not known yet',
};

/**
 * The property a client holds (Arthur, 6 October): a house they own while they
 * live in a nursing home, a lot they inherited, the apartment they rent.
 *
 * **Adding one records the holding and nothing else.** No placement, no move:
 * a property is never recorded by inventing a placement, and a placement is
 * never derived from a property. Before this, the only way to create a property
 * was to move the client into it.
 *
 * Shown to whoever may see the client. Adding is `property` write (tiers 1 and
 * 2 today); the server is the rule, and `may_record` only hides the button.
 */
export function PropertiesPanel({ clientId }: { clientId: string }) {
  const q = useClientProperties(clientId);
  const [adding, setAdding] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  // The old opener (122, A-#191): ?visit=<property id> REDIRECTS to the property's own page (A-#195 1), keeping a
  // request for its Record a site visit form, so no link kept from before breaks.
  const [params] = useSearchParams();
  const { state } = useLocation();
  const asked = params.get('visit');
  if (asked) {
    return <Navigate to={`/clients/${clientId}/property/${asked}`} replace state={(state as RecordOpen | null) ?? undefined} />;
  }

  if (!q.data) {
    return q.error ? (
      <section className="panel px-4 py-3">
        <p className="text-sm text-rose-700">The client's properties could not be loaded.</p>
      </section>
    ) : null;
  }
  const { may_read: mayRead, may_record: mayRecord, properties, property_types: types } = q.data;
  if (!mayRead) return null;
  const held = properties.filter((p) => !p.discharged);
  const discharged = properties.filter((p) => p.discharged);
  if (properties.length === 0 && !mayRecord) return null;

  return (
    <section className="panel">
      <h2 className="panel-bar">
        Property
      </h2>
      <div className="px-4 pb-3">
        {held.length === 0 && <p className="text-sm text-neutral-600">No property held is on record.</p>}
        {held.length > 0 && (
          <ul className="divide-y divide-neutral-100">
            {held.map((p) => <li key={p.id} className="py-1"><Row p={p} to={propertyPath(clientId, p.id)} /></li>)}
          </ul>
        )}
        {discharged.length > 0 && (
          <details className="mt-2">
            <summary className="min-h-[44px] cursor-pointer py-2 text-sm font-semibold text-neutral-700">
              Inactive ({discharged.length}): sold, surrendered, or the client discharged
            </summary>
            <ul className="divide-y divide-neutral-100">
              {discharged.map((p) => <li key={p.id} className="py-1"><Row p={p} to={propertyPath(clientId, p.id)} /></li>)}
            </ul>
          </details>
        )}

        {said && <p role="status" className="mt-2 rounded-lg bg-neutral-50 px-3 py-2 text-neutral-800">{said}</p>}

        {mayRecord && !adding && (
          <button type="button" className={`${secondary} mt-2`} onClick={() => { setAdding(true); setSaid(null); }}>
            Add a property
          </button>
        )}
        {adding && (
          <AddProperty
            clientId={clientId}
            types={types}
            onDone={(message) => { setAdding(false); setSaid(message); }}
          />
        )}
      </div>
    </section>
  );
}

/** A property in the client's list: a link to its own page (A-#195 1). */
function Row({ p, to }: { p: ClientProperty; to: string }) {
  return (
    <Link to={to}
          className="flex min-h-[44px] w-full flex-wrap items-start justify-between gap-x-4 gap-y-1 rounded-lg px-1 py-1 text-left text-neutral-900 hover:bg-neutral-50">
      <div>
        <PropertyAddress street={p.street} city={p.city} state={p.state} zip={p.zip}
                         neighbourhood={p.neighbourhood} neighbourhoodSource={p.neighbourhood_source} />
        {p.occupied && <OccupiedFlag />}
      </div>
      <div className="text-sm text-neutral-700 sm:text-right">
        <span className="block">{p.property_type_label ?? 'Kind not recorded'} · {TENURE_WORDS[p.tenure] ?? p.tenure}</span>
        {p.discharged ? (
          <span className="block">
            {p.disposition ?? 'No disposition'}, inactive {p.discharged_on ? `since ${formatDate(p.discharged_on)}` : '(date not known)'}
          </span>
        ) : p.disposition ? <span className="block">{p.disposition}</span> : null}
      </div>
    </Link>
  );
}

/** Derived on every read: the client lives here now and is alive. Never stored. */
export function OccupiedFlag() {
  return (
    <span className="mt-1 inline-block rounded border border-neutral-400 px-1.5 py-0.5 text-xs font-semibold tracking-wide text-neutral-800 uppercase">
      Occupied by client
    </span>
  );
}

interface Draft {
  property_type: string;
  tenure: '' | Tenure;
  street_address: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  neighbourhood: string;
  parcel_id: string;
  legal_description: string;
  lis_pendens_filed: boolean;
  court_auth_required: boolean;
  notes: string;
  held_since: string;
}

const EMPTY: Draft = {
  property_type: '', tenure: '', street_address: '', unit: '', city: '', state: 'New York', zip: '',
  county: '', neighbourhood: '', parcel_id: '', legal_description: '', lis_pendens_filed: false,
  court_auth_required: true, notes: '', held_since: '',
};

function toInput(d: Draft): NewProperty | null {
  if (!d.property_type || !d.tenure || !d.street_address.trim() || !d.city.trim() ||
      d.state.trim().length < 2 || (d.property_type === 'Other' && !d.notes.trim())) {
    return null;
  }
  const opt = (k: keyof Draft, v: string) => (v.trim() ? { [k]: v.trim() } : {});
  return {
    property_type: d.property_type,
    tenure: d.tenure,
    street_address: d.street_address.trim(),
    city: d.city.trim(),
    state: d.state.trim(),
    ...opt('unit', d.unit), ...opt('zip', d.zip), ...opt('county', d.county),
    ...opt('neighbourhood', d.neighbourhood), ...opt('parcel_id', d.parcel_id),
    ...opt('legal_description', d.legal_description), ...opt('notes', d.notes), ...opt('held_since', d.held_since),
    lis_pendens_filed: d.lis_pendens_filed,
    court_auth_required: d.court_auth_required,
  };
}

function AddProperty({
  clientId, types, onDone,
}: { clientId: string; types: PropertyType[]; onDone: (message: string | null) => void }) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const add = useAddProperty(clientId);
  // A second click in the same tick, before the button disables, would send
  // twice (RecordVisit, 17 September). Released on a real failure.
  const sending = useRef(false);
  const input = toInput(draft);

  const submit = () => {
    if (!input || sending.current) return;
    sending.current = true;
    add.mutate(input, {
      onSuccess: (r) => {
        const owed = r.site_visits_open_after > r.site_visits_open_before;
        onDone(owed
          ? 'Recorded. Because they own or rent it, a site visit to the property is now owed.'
          : 'Recorded.');
      },
      onError: () => { sending.current = false; },
    });
  };

  return (
    <form className="mt-3 space-y-3 rounded-lg border border-neutral-300 p-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <p className="text-sm text-neutral-700">
        A property the client holds. This records the holding only: it does not say they live there.
        If they are moving into it, record the move instead.
      </p>
      <PropertyFields draft={draft} onChange={setDraft} types={types} />

      {(draft.tenure === 'Owned' || draft.tenure === 'Rented') && (
        <>
          <p className={hint}>
            Owned or rented property is visited: recording it may make a site visit owed.
          </p>
          <div>
            <label className={label} htmlFor="property-held-since">
              Held since <span className="font-normal text-neutral-500">— only if they came to hold it after the agency was appointed</span>
            </label>
            <input id="property-held-since" type="date" className={field} value={draft.held_since}
                   max={new Date().toLocaleDateString('en-CA')}
                   onChange={(e) => setDraft((d) => ({ ...d, held_since: e.target.value }))} />
            <p className={hint}>
              Leave it empty if they held it before, or nobody knows: the site visit is then owed from the appointment.
            </p>
          </div>
        </>
      )}

      {add.error && <Refusal error={add.error} />}

      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!input || add.isPending}>
          {add.isPending ? 'Recording…' : 'Record the property'}
        </button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
      {!input && <p className={hint}>A kind, a tenure, a street, a city and a state are needed.</p>}
    </form>
  );
}

function PropertyFields({
  draft, onChange, types,
}: { draft: Draft; onChange: Dispatch<SetStateAction<Draft>>; types: PropertyType[] }) {
  const set = <K extends keyof Draft>(k: K) => (v: Draft[K]) => onChange((d) => ({ ...d, [k]: v }));
  return (
    <>
      <div>
        <label className={label} htmlFor="property-type">What kind of property</label>
        <select id="property-type" className={field} value={draft.property_type}
                onChange={(e) => set('property_type')(e.target.value)}>
          <option value="">Choose…</option>
          {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
      </div>

      <fieldset>
        <legend className={label}>How do they hold it?</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {TENURES.map((t) => (
            <button key={t} type="button" onClick={() => set('tenure')(t)} aria-pressed={draft.tenure === t}
                    className={`${secondary} ${draft.tenure === t ? '!bg-neutral-900 !text-white' : ''}`}>
              {TENURE_WORDS[t]}
            </button>
          ))}
        </div>
      </fieldset>

      <AddressFields
        prefix="property"
        street={draft.street_address} setStreet={set('street_address')}
        unit={draft.unit} setUnit={set('unit')}
        city={draft.city} setCity={set('city')}
        state={draft.state} setState={set('state')}
        zip={draft.zip} setZip={set('zip')}
        county={draft.county} setCounty={set('county')}
      />
      <NeighbourhoodField id="property-neighbourhood" value={draft.neighbourhood} onChange={set('neighbourhood')} />

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="property-parcel">
            Parcel ID <span className="font-normal text-neutral-500">— if known</span>
          </label>
          <input id="property-parcel" className={field} value={draft.parcel_id} maxLength={64}
                 onChange={(e) => set('parcel_id')(e.target.value)} autoComplete="off" />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="property-legal">
          Legal description <span className="font-normal text-neutral-500">— if known</span>
        </label>
        <textarea id="property-legal" className={`${field} min-h-[72px] py-2`} value={draft.legal_description}
                  maxLength={4000} onChange={(e) => set('legal_description')(e.target.value)} />
      </div>

      <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1 size-5 shrink-0" checked={draft.lis_pendens_filed}
               onChange={(e) => set('lis_pendens_filed')(e.target.checked)} />
        <span className="text-neutral-800">A lis pendens has been filed against it</span>
      </label>
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1 size-5 shrink-0" checked={draft.court_auth_required}
               onChange={(e) => set('court_auth_required')(e.target.checked)} />
        <span className="text-neutral-800">
          A sale or disposition needs the court's authority
          <span className="block text-sm text-neutral-600">
            On unless the order says otherwise: a sale or surrender is made on a petition to the court.
          </span>
        </span>
      </label>

      <div>
        <label className={label} htmlFor="property-notes">
          Notes{' '}
          <span className="font-normal text-neutral-500">
            {draft.property_type === 'Other' ? '— say what kind of property it is' : '— optional'}
          </span>
        </label>
        <textarea id="property-notes" className={`${field} min-h-[88px] py-2`} value={draft.notes} maxLength={4000}
                  onChange={(e) => set('notes')(e.target.value)} />
      </div>
    </>
  );
}

export function Refusal({ error }: { error: Error }) {
  const hintText = error instanceof ApiError ? error.hint : undefined;
  return (
    <div role="alert" className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900">
      <p className="font-semibold">{error.message}</p>
      {hintText && <p className="mt-1 text-sm">{hintText}</p>}
    </div>
  );
}
